/**
 * CartSlideOutV3 — Full cart slide-out panel for GearFocus Checkout V3.
 *
 * Built from:
 *  - Production ShoppingCart.tsx (context hooks, GA4, BaseDrawer)
 *  - Prototype cart-slideout.tsx (layout/sections/design reference)
 *  - Checkout-v3 components (condition badges, seller resolution, styling)
 *
 * Sections (desktop): Header, Buyer Protection, Seller-grouped Items,
 * Pricing Summary, Affirm, Continue Shopping, Sticky Footer.
 *
 * Mobile hides: Continue Shopping, Shipping/Tax rows. Affirm messaging
 * shows on mobile too (GB #1372 — mobile converts 3x desktop; hiding
 * financing there was backwards).
 */

import { AffirmMessaging } from '@c/affirm/AffirmMessaging';
import { isAffirmDisplayable, normalizeCountryCode } from '@util/affirm/eligibility';
import BaseDrawer from '@c/drawers/BaseDrawer';
import ProtectionPolicyModal from '@c/modals/ProtectionPolicyModal';
import CloseButton from '@c/icons/buttons/CloseButton';
import { CartIcon } from '@c/icons';
import Button from '@ui/Button';
import SafeImage from '@ui/SafeImage';
import { formatCartItems, trackEvent } from '@util/analytics';
import {
  CART_TELEMETRY_EVENTS,
  CART_TELEMETRY_FINGERPRINTS,
  buildCartCommonContext,
  emitCartTelemetry,
  isDrawerEmptyMismatch,
} from '@util/cartTelemetry';
import { formatCurrency, getCartItemFromProductDocument } from '@util/index';
import { useStoreSales } from 'context/StoreSaleContext';
import { getProductUrl } from '@util/urlHelpers';
import { getUsersByIds } from '@util/firestore/users/user.service';
import { addProductToFavorites } from '@util/firestore/products';
import { useAuth } from 'context/AuthContext';
import { useShoppingCart } from 'context/ShoppingCartContext';
import { useQuery } from '@tanstack/react-query';
import Link from 'next/link';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getConditionShortLabel } from '@util/conditions/conditionShortLabel';

// ---------------------------------------------------------------------------
// Inline SVG icons (avoid cross-file import churn)
// ---------------------------------------------------------------------------

/** GearFocus shield-check used for Buyer Protection and Verified badges */
function GFShieldCheck({ size = 16, fill = '#1B1B1B' }: { size?: number; fill?: string }) {
  return (
    <svg
      width={size}
      height={size * 1.2}
      viewBox="0 0 18 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="shrink-0"
    >
      <path
        d="M7 16L3 12L4.41 10.59L7 13.17L13.59 6.58L15 8M9 0L0 4V10C0 15.55 3.84 20.74 9 22C14.16 20.74 18 15.55 18 10V4L9 0Z"
        fill={fill}
      />
    </svg>
  );
}

function TruckIcon({ size = 14, className = '' }: { size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`shrink-0 ${className}`}
    >
      <path d="M1 3h15v13H1zM16 8h4l3 3v5h-7V8z" />
      <circle cx="5.5" cy="18.5" r="1.5" />
      <circle cx="18.5" cy="18.5" r="1.5" />
    </svg>
  );
}

function CalendarIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function PackageIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0"
    >
      <path d="M16.5 9.4l-9-5.19M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
      <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
      <line x1="12" y1="22.08" x2="12" y2="12" />
    </svg>
  );
}

function HeartIcon({ size = 12 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0"
    >
      <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
    </svg>
  );
}

function XIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function ArrowLeftIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0"
    >
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const AVATAR_COLORS = ['#4A90D9', '#E8600A', '#2E7D32', '#7B1FA2', '#C62828', '#00838F'];

/** Deterministic color from seller ID */
function getAvatarColor(sellerId: string): string {
  let hash = 0;
  for (let i = 0; i < sellerId.length; i++) {
    hash = sellerId.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

/** Condition badge color (reused from checkout-v3 OrderSummaryV3/SellerShippingGroup) */
function getConditionColor(condition: string): string {
  const c = condition.toLowerCase();
  if (c === 'new' || c.includes('refurbished') || c.includes('unopened')) return 'bg-green-100 text-green-700';
  if (c.includes('like new') || c.includes('excellent')) return 'bg-blue-100 text-brand-blue';
  if (c.includes('good')) return 'bg-gray-200 text-gray-700';
  if (c.includes('fair')) return 'bg-yellow-100 text-yellow-600';
  if (c.includes('poor')) return 'bg-red-100 text-red-600';
  return 'bg-blue-100 text-brand-blue';
}

/** Estimated delivery: 5-9 business days from today */
function getDeliveryRange(): string {
  const addBusinessDays = (days: number): Date => {
    const date = new Date();
    let added = 0;
    while (added < days) {
      date.setDate(date.getDate() + 1);
      if (date.getDay() !== 0 && date.getDay() !== 6) added++;
    }
    return date;
  };
  const start = addBusinessDays(5);
  const end = addBusinessDays(9);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[start.getMonth()]} ${start.getDate()}\u2013${months[end.getMonth()]} ${end.getDate()}`;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Compact single-line Buyer Protection bar */
function BuyerProtectionBar({ onLearnMore }: { onLearnMore: () => void }) {
  return (
    <div className="flex items-center gap-[8px] rounded-[8px] border border-[#D4DCE8] bg-[#EEF0F8] px-[12px] py-[10px]">
      <GFShieldCheck size={16} fill="#242E5F" />
      <p className="text-[12px] leading-tight text-[#1B1B1B]">
        <span className="font-semibold">GearFocus Buyer Protection</span>
        {' '}&mdash; Your money back if not as described.{' '}
        <button onClick={onLearnMore} className="font-medium text-brand-primary hover:underline">
          Learn more
        </button>
      </p>
    </div>
  );
}

/** Condition badge — matches checkout-v3 pattern */
function ConditionBadge({ condition }: { condition?: string }) {
  if (!condition) return null;
  return (
    <span
      className={`inline-flex w-fit items-center rounded-full px-[8px] py-[1px] text-[10px] font-semibold ${getConditionColor(condition)}`}
    >
      {getConditionShortLabel(condition)}
    </span>
  );
}

/** Undo row — shown for 5 seconds after removing an item */
function RemovedItemRow({
  thumbnail,
  title,
  onUndo,
  disabled,
}: {
  thumbnail: string;
  title: string;
  onUndo: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center gap-[10px] rounded-[8px] border border-gray-200 bg-gray-50 px-[12px] py-[10px]">
      <div className="h-[36px] w-[36px] shrink-0 overflow-hidden rounded-[4px] border border-gray-200 bg-gray-50">
        <SafeImage alt={title} src={thumbnail} className="h-full w-full object-contain" />
      </div>
      <span className="flex-1 text-[13px] text-gray-500">Item removed.</span>
      <button
        onClick={onUndo}
        disabled={disabled}
        className="text-[13px] font-semibold text-brand-primary hover:underline disabled:cursor-not-allowed disabled:text-gray-300 disabled:no-underline"
      >
        Undo
      </button>
    </div>
  );
}

/** Payment icons row */
function PaymentIcons() {
  const brands = [
    { src: '/cc_brands/visa.png', alt: 'Visa' },
    { src: '/cc_brands/mastercard.png', alt: 'Mastercard' },
    { src: '/cc_brands/amex.png', alt: 'Amex' },
    { src: '/cc_brands/discover.png', alt: 'Discover' },
    { src: '/cc_brands/apple-pay.webp', alt: 'Apple Pay' },
    { src: '/cc_brands/google-pay.webp', alt: 'Google Pay' },
  ];
  return (
    <div className="flex items-center justify-center gap-[8px]">
      {brands.map((b) => (
        <img key={b.alt} src={b.src} alt={b.alt} className="h-[24px] w-auto" />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const CartSlideOutV3 = () => {
  const { userDoc } = useAuth();
  const { getEffectivePriceForProduct } = useStoreSales();
  const {
    cartOpen,
    setCartOpen,
    cart,
    realTimeProducts,
    removeFromCart,
    increaseCartQty,
    decreaseCartQty,
    addingProductIds,
  } = useShoppingCart();

  // ---- Undo-remove state ----
  const [undoableIds, setUndoableIds] = useState<Set<string>>(new Set());
  const [protectionModalOpen, setProtectionModalOpen] = useState(false);
  // Product ids whose quantity-stepper increment is awaiting its fetch RTT.
  // increaseCartQty holds an in-flight lock and silently no-ops concurrent
  // calls, so the stepper must disable itself while an increment is pending —
  // otherwise rapid + clicks are dropped with no feedback (GB #1323).
  const [incrementingIds, setIncrementingIds] = useState<Set<string>>(new Set());

  const undoTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Snapshot of removed items for undo display (thumbnail + title). Stores the
  // full cart item so undo can re-add it, plus the auth identity at removal
  // time (D4) so an undo after an auth change is refused rather than leaking an
  // auth-removed item into the guest cart.
  const [removedSnapshots, setRemovedSnapshots] = useState<Map<string, { thumbnail: string; title: string; cartItem: any; auth: string }>>(new Map());

  // Current auth identity used to tag/compare undo snapshots.
  const authId = userDoc?.uid ?? 'guest';

  const clearUndoRow = useCallback((productId: string) => {
    const timer = undoTimers.current.get(productId);
    if (timer) {
      clearTimeout(timer);
      undoTimers.current.delete(productId);
    }
    setUndoableIds((prev) => {
      const next = new Set(prev);
      next.delete(productId);
      return next;
    });
    setRemovedSnapshots((prev) => {
      const next = new Map(prev);
      next.delete(productId);
      return next;
    });
  }, []);

  // (Re)start the 5s auto-dismiss countdown for an undo row.
  const armUndoTimer = useCallback((productId: string) => {
    const existing = undoTimers.current.get(productId);
    if (existing) clearTimeout(existing);
    const timer = setTimeout(() => clearUndoRow(productId), 5000);
    undoTimers.current.set(productId, timer);
  }, [clearUndoRow]);

  const handleRemove = useCallback((productId: string, thumbnail: string, title: string, cartItem: any) => {
    // Remove from cart IMMEDIATELY (fixes: closing drawer before timer = item persists)
    removeFromCart(productId);

    // Save snapshot for undo row (full cart item + auth identity for re-adding).
    setRemovedSnapshots((prev) => new Map(prev).set(productId, { thumbnail, title, cartItem, auth: authId }));
    setUndoableIds((prev) => new Set(prev).add(productId));
    armUndoTimer(productId);
  }, [removeFromCart, armUndoTimer, authId]);

  const handleUndoRemove = useCallback(async (productId: string) => {
    const snapshot = removedSnapshots.get(productId);
    if (!snapshot?.cartItem) return;

    // D4: auth state changed since removal (e.g. logout between remove and undo).
    // Re-adding would leak an auth-removed item into the guest localStorage cart
    // (or vice versa). Refuse the re-add and just clear the row.
    if (snapshot.auth !== authId) {
      clearUndoRow(productId);
      return;
    }

    // increaseCartQty returns a status: tear down the undo affordance only if the
    // add landed ('added') or was surfaced to the user ('blocked', e.g. stock
    // cap). If 'dropped' — swallowed by the in-flight lock (a concurrent add for
    // the same product) — the item was NOT restored, so keep the undo row and
    // RE-ARM its timer so the original 5s countdown can't burn it (D2). The undo
    // button is also disabled while that id is in flight.
    const result = await increaseCartQty(snapshot.cartItem, true);
    if (result === 'dropped') {
      armUndoTimer(productId);
      return;
    }
    clearUndoRow(productId);
  }, [increaseCartQty, removedSnapshots, authId, clearUndoRow, armUndoTimer]);

  // Clean up timers on unmount
  useEffect(() => {
    return () => {
      undoTimers.current.forEach((t) => clearTimeout(t));
    };
  }, []);

  // ---- Map product_id -> realtime product doc for condition, slug, images ----
  const productMap = useMemo(
    () => new Map(realTimeProducts.map((p) => [p.id, p])),
    [realTimeProducts],
  );

  // ---- Visible items: only show products that still exist in realtime Firestore data.
  // Matches V2 behavior — deleted/unavailable products are silently hidden. ----
  const visibleItems = useMemo(
    () => cart.items.filter((item) => productMap.has(item.product_id)),
    [cart.items, productMap],
  );

  // ---- Group items by seller ----
  const sellerGroups = useMemo(() => {
    const groups = new Map<string, typeof cart.items>();
    // Use all cart items (not just visible) so undo rows appear in the right group
    for (const item of cart.items) {
      const sellerId = item.seller_id || 'unknown';
      const list = groups.get(sellerId) ?? [];
      list.push(item);
      groups.set(sellerId, list);
    }
    return groups;
  }, [cart.items]);

  const isMultiSeller = sellerGroups.size > 1;

  // ---- Seller name resolution (same pattern as OrderSummaryV3) ----
  const sellerIds = useMemo(
    () => [...new Set(cart.items.map((i) => i.seller_id).filter(Boolean))],
    [cart.items],
  );

  const { data: sellerUsers = [] } = useQuery({
    queryKey: ['cartSlideoutSellers', sellerIds],
    queryFn: () => getUsersByIds(sellerIds),
    enabled: sellerIds.length > 0,
    staleTime: 1000 * 60 * 10,
  });

  const sellerNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const user of sellerUsers) {
      if (user.uid) map.set(user.uid, user.username ?? user.uid.slice(0, 8) + '\u2026');
    }
    for (const id of sellerIds) {
      if (!map.has(id)) map.set(id, id.slice(0, 8) + '\u2026');
    }
    return map;
  }, [sellerUsers, sellerIds]);

  // ---- Pricing (use realtime price, fall back to snapshot) ----
  const getItemPrice = useCallback(
    (item: typeof cart.items[number]) => {
      const product = productMap.get(item.product_id);
      if (!product) return item.product_cost;
      const pricing = getEffectivePriceForProduct(product);
      return pricing?.applied ? pricing.unitCents / 100 : product.price;
    },
    [productMap, getEffectivePriceForProduct],
  );

  // ---- Buyer's saved country (best-effort) ----
  // Single derivation shared by the displayed shipping band below AND the
  // Affirm messaging mount further down (GB #1372 QA follow-up) — one value
  // so the two can never disagree about which country the drawer thinks the
  // buyer is in. Guests/no-address stay undefined, which is each call site's
  // existing default behavior.
  //
  // Each side is normalized individually before the `??` fallback (Codex
  // P2, GB #1372 QA follow-up): getValidUserDocument initializes new users
  // with country: '' (not null/undefined), and `??` does not fall through
  // on ''. A raw `userDoc?.country ?? userDoc?.addresses?.[0]?.country_code`
  // would silently lock onto that '' and never even look at the buyer's
  // saved address. normalizeCountryCode collapses '' (and any
  // whitespace-only value) to undefined first, so `??` only skips a truly
  // absent country.
  //
  // SECOND BEHAVIOR CHANGE riding this same line (fresh-eyes review on PR
  // #1451, flagged explicitly so it isn't missed as a side effect of the
  // Affirm fix): getDisplayShipping below already consumed this same
  // buyerCountry value before this PR, with the same '' bug. A buyer with
  // country: '' and a non-US saved address previously had their displayed
  // shipping band silently fall back to bands[0] (the Continental-US base
  // entry) instead of their actual address's band — normalizeCountryCode
  // fixes that too, as a consequence of centralizing the derivation. This
  // is a genuine improvement (the displayed shipping now matches the
  // buyer's saved address), not a regression, but it IS a second,
  // independently-observable behavior change beyond Affirm messaging.
  const buyerCountry =
    normalizeCountryCode(userDoc?.country) ??
    normalizeCountryCode(userDoc?.addresses?.[0]?.country_code);

  // ---- Displayed flat-rate shipping (DISPLAY ONLY) ----
  // Mirror the product page (ProductDetails.tsx): derive the displayed base
  // shipping band from the PRODUCT's shipping_costs, NOT from item.shipping_cost.
  // item.shipping_cost is an unresolved 0 until an address is entered (the cart
  // drawer never has one), which previously made a $15 item look "Free".
  // For logged-in users with a saved country, refine to the matching band;
  // guests (and no-match) fall back to the Continental-US base entry [0].
  const getDisplayShipping = useCallback(
    (productId: string) => {
      const product = productMap.get(productId);
      const bands = product?.shipping_costs;
      if (!bands || bands.length === 0) return undefined;
      const matched = buyerCountry ? bands.find((b) => b.code === buyerCountry) : undefined;
      return matched ?? bands[0];
    },
    [productMap, buyerCountry],
  );

  const subtotal = visibleItems.reduce(
    (acc, item) => acc + item.qty * getItemPrice(item),
    0,
  );

  const shippingTotal = visibleItems.reduce((acc, item) => {
    if (item.is_flat_rate && item.shipping_cost != null) return acc + item.shipping_cost;
    return acc;
  }, 0);

  // Genuine-free ONLY when EVERY visible flat-rate item's PRODUCT base rate is
  // resolved to 0 (computed from product.shipping_costs, not item.shipping_cost).
  // An item with no shipping_costs (or product not loaded) is NOT genuine-free.
  const allFreeShipping = visibleItems.every((item) => {
    if (!item.is_flat_rate) return false;
    const base = getDisplayShipping(item.product_id);
    return base != null && base.cost === 0;
  });

  const estimatedTotal = subtotal + shippingTotal;

  // ---- GA4 view_cart event (mirrors production ShoppingCart.tsx) ----
  useEffect(() => {
    if (!cartOpen || !cart?.items?.length) return;
    trackEvent('view_cart', {
      currency: 'USD',
      value: cart.subtotal,
      items: formatCartItems(cart.items),
    });
  }, [cartOpen]);

  // ---- Delivery range (computed once) ----
  const deliveryRange = useMemo(() => getDeliveryRange(), []);

  // ---- Close drawer handler ----
  const closeDrawer = useCallback(() => setCartOpen(false), [setCartOpen]);

  // ---- Check if cart has any visible items or undo placeholders ----
  const hasUndoPlaceholders = undoableIds.size > 0;
  const isEmpty = visibleItems.length === 0 && !hasUndoPlaceholders;

  // ==========================================================================
  // E1 — cart_drawer_empty_mismatch (TASK-1323 §4), M1 case. PURE TELEMETRY.
  //
  // Fires when the drawer renders its FULL empty state (`isEmpty`) even though
  // `cart.items` is non-empty — the realtime-snapshot gap where a just-added
  // item's product isn't in `productMap` yet.
  //
  // The 2026-07-15 remove-path desync (`items: []` but `product_ids`/`subtotal`
  // stale) is NOT detectable here: the drawer consumes the `calcCart`-derived
  // cart, which recomputes `product_ids` and `subtotal` from `items` every
  // render, so at the drawer those fields are always in sync with `items`. That
  // desync is caught at the raw-cart flush site in ShoppingCartContext (see
  // `detectRawCartDesync`).
  //
  // Once per drawer-open. On mismatch we wait up to 2s: if it self-heals (the
  // realtime snapshot lands and clears `isEmpty`) we report healed:true +
  // heal_ms; if it is still mismatched at 2s we report healed:false (the M2/M3
  // signature — a wedge or a lost item that will not self-heal).
  // ==========================================================================
  const e1Mismatch =
    cartOpen && isDrawerEmptyMismatch({ isEmpty, itemsLength: cart.items.length });

  const e1FiredRef = useRef(false);
  const e1StartRef = useRef<number | null>(null);
  const e1TimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const e1CtxRef = useRef<Record<string, unknown>>({});

  // Keep a fresh context snapshot for the deferred (timer) fire path.
  const cartProductIds = useMemo(
    () => new Set(cart.items.map((i) => i.product_id)),
    [cart.items],
  );
  const e1RealtimeStatus: 'live' | 'loading' =
    cart.items.length > 0 &&
    [...cartProductIds].every((id) => productMap.has(id))
      ? 'live'
      : 'loading';
  e1CtxRef.current = {
    ...buildCartCommonContext({
      auth: userDoc?.uid ? 'user' : 'guest',
      cartSize: cart.items.length,
      productMapSize: productMap.size,
      realtimeStatus: e1RealtimeStatus,
      sellerCount: sellerGroups.size,
    }),
    source: 'drawer_m1',
    // NOTE: no ms-since-last-add here. The only timestamp in scope is the
    // `calcCart`-derived `cart.last_updated`, which is re-stamped Date.now()
    // every render — so any delta computed from it is meaningless. `heal_ms`
    // (measured from first-mismatch to heal below) is the real timing signal.
  };

  const fireE1 = useCallback(
    (healed: boolean, healMs: number | null) => {
      if (e1FiredRef.current) return;
      e1FiredRef.current = true;
      emitCartTelemetry(
        CART_TELEMETRY_EVENTS.DRAWER_EMPTY_MISMATCH,
        { ...e1CtxRef.current, healed, heal_ms: healMs },
        // Only the un-healed case is actionable engineering signal — keep the
        // transient (healed:true) case out of the Sentry issue stream. GA4
        // still receives both so the heal-rate metric is measurable.
        healed ? undefined : { fingerprint: CART_TELEMETRY_FINGERPRINTS.DRAWER_EMPTY },
      );
    },
    [],
  );

  useEffect(() => {
    // Reset per drawer-open so the "once per open" guard re-arms each time.
    if (!cartOpen) {
      e1FiredRef.current = false;
      e1StartRef.current = null;
      if (e1TimerRef.current) {
        clearTimeout(e1TimerRef.current);
        e1TimerRef.current = null;
      }
      return;
    }
    if (e1FiredRef.current) return;

    if (e1Mismatch) {
      // First time we see the mismatch this open: start the heal clock.
      if (e1StartRef.current == null) {
        e1StartRef.current = Date.now();
        e1TimerRef.current = setTimeout(() => {
          // Still mismatched at +2s → does not self-heal.
          fireE1(false, null);
        }, 2000);
      }
    } else if (e1StartRef.current != null) {
      // Was mismatched, now healed before the 2s deadline.
      const healMs = Date.now() - e1StartRef.current;
      if (e1TimerRef.current) {
        clearTimeout(e1TimerRef.current);
        e1TimerRef.current = null;
      }
      fireE1(true, healMs);
    }
  }, [cartOpen, e1Mismatch, fireE1]);

  // Clear any pending E1 timer on unmount.
  useEffect(() => {
    return () => {
      if (e1TimerRef.current) clearTimeout(e1TimerRef.current);
    };
  }, []);

  return (
    <>
    <BaseDrawer
      show={cartOpen}
      side="right"
      dismiss={closeDrawer}
    >
      <div className="flex h-full w-full flex-col bg-white font-sans text-[#1B1B1B]">
        {/* ================================================================
            FIXED HEADER
        ================================================================ */}
        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-[20px] py-[18px]">
          <h2 className="text-[18px] font-semibold">
            {visibleItems.length === 0 ? 'Your Cart' : `Your Cart (${visibleItems.reduce((sum, i) => sum + i.qty, 0)} ${visibleItems.reduce((sum, i) => sum + i.qty, 0) === 1 ? 'item' : 'items'})`}
          </h2>
          <CloseButton onClick={closeDrawer} />
        </div>

        {/* ================================================================
            EMPTY STATE
        ================================================================ */}
        {isEmpty && (
          <div className="flex flex-1 flex-col items-center justify-center gap-[24px] px-[24px] py-[48px]">
            <div className="text-gray-300">
              <CartIcon width={56} height={56} />
            </div>
            <div className="flex flex-col items-center gap-[8px] text-center">
              <p className="text-[16px] font-semibold">Your cart is empty</p>
              <p className="text-[14px] text-gray-500">
                Browse our collection of premium camera gear
              </p>
            </div>
            <Button
              text="Browse Listings"
              href="/products"
              type="secondary"
              onClick={closeDrawer}
            />
          </div>
        )}

        {/* ================================================================
            CART WITH ITEMS
        ================================================================ */}
        {!isEmpty && (
          <>
            {/* ---- Scrollable body ---- */}
            <div className="flex-1 overflow-y-auto px-[20px] pb-[24px] pt-[16px]">

              {/* 1. Buyer Protection Bar */}
              <BuyerProtectionBar onLearnMore={() => setProtectionModalOpen(true)} />

              {/* Undo rows for recently removed items (rendered here, outside seller
                  groups, because the item is already removed from cart.items) */}
              {[...undoableIds].map((pid) => {
                const snapshot = removedSnapshots.get(pid);
                if (!snapshot) return null;
                return (
                  <div key={`undo-${pid}`} className="mt-[12px] rounded-[8px] border border-gray-200 px-[14px] py-[12px]">
                    <RemovedItemRow
                      thumbnail={snapshot.thumbnail}
                      title={snapshot.title}
                      onUndo={() => handleUndoRemove(pid)}
                      disabled={addingProductIds.has(pid)}
                    />
                  </div>
                );
              })}

              {/* 2. Seller-grouped items */}
              <div className="mt-[16px] flex flex-col gap-[16px]">
                {[...sellerGroups.entries()].map(([sellerId, items]) => {
                  const sellerName = sellerNameMap.get(sellerId) ?? sellerId.slice(0, 8) + '\u2026';
                  const initial = sellerName.charAt(0).toUpperCase();
                  const avatarColor = getAvatarColor(sellerId);

                  // Only render group if it has visible items or undo placeholders
                  if (items.length === 0) return null;

                  return (
                    <div key={sellerId} className="rounded-[8px] border border-gray-200">
                      {/* Seller header */}
                      <div className="flex items-center gap-[8px] border-b border-gray-200 bg-gray-50 px-[14px] py-[10px]">
                        {/* Avatar circle */}
                        <div
                          className="flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-full text-[13px] font-bold text-white"
                          style={{ backgroundColor: avatarColor }}
                        >
                          {initial}
                        </div>
                        {/* Seller name + ships separately */}
                        <div className="flex flex-col">
                          <span className="text-[13px] font-bold text-[#1B1B1B]">{sellerName}</span>
                          {isMultiSeller && (
                            <span className="flex items-center gap-[4px] text-[10px] text-gray-400">
                              <PackageIcon size={10} />
                              Ships separately
                            </span>
                          )}
                        </div>
                        {/* Verified badge */}
                        <span className="ml-auto flex items-center gap-[4px] text-[11px] font-medium text-[#1B1B1B]">
                          <GFShieldCheck size={12} fill="#1B1B1B" />
                          Verified By GearFocus
                        </span>
                      </div>

                      {/* Item cards + undo rows */}
                      <div className="flex flex-col divide-y divide-gray-100 px-[14px]">
                        {items.map((item) => {
                            const product = productMap.get(item.product_id);
                            const slug = product?.slug ?? item.product_id;

                            const currentPrice = getItemPrice(item);
                            const storeSale = product && getEffectivePriceForProduct(product)?.applied;
                            const originalPrice = storeSale ? product.price
                              : product?.on_sale && product.og_price > product.price ? product.og_price : undefined;
                            const hasOriginalPrice = originalPrice != null && originalPrice > currentPrice;

                            // DISPLAY ONLY: derive shipping from the product base
                            // band (same source as the product page), NOT from
                            // item.shipping_cost (an unresolved 0 in the drawer).
                            const shippingBase = item.is_flat_rate
                              ? getDisplayShipping(item.product_id)
                              : undefined;
                            // Genuine-free only when the base band resolved to 0.
                            const isFreeShipping = shippingBase != null && shippingBase.cost === 0;

                            return (
                              <div key={item.product_id} className="relative py-[14px]">
                                {/* X remove button — absolutely positioned, centered with image */}
                                <button
                                  onClick={() =>
                                    handleRemove(item.product_id, item.product_image, item.product_title, item)
                                  }
                                  disabled={addingProductIds.has(item.product_id)}
                                  className="absolute left-[-7px] top-[14px] flex h-[78px] w-[14px] items-center justify-center text-gray-400 hover:text-gray-600 disabled:cursor-not-allowed disabled:text-gray-200"
                                  aria-label={`Remove ${item.product_title}`}
                                >
                                  <XIcon size={13} />
                                </button>

                                <div className="flex gap-[8px] pl-[11px]">
                                  {/* Product image column */}
                                  <div className="flex shrink-0 flex-col items-center gap-[6px]">
                                    <Link
                                      href={getProductUrl(slug)}
                                      onClick={closeDrawer}
                                      className="h-[78px] w-[78px] overflow-hidden rounded-[6px] border border-[#D0D0D0] bg-gray-50"
                                    >
                                      <SafeImage
                                        alt={item.product_title}
                                        src={item.product_image}
                                        className="h-full w-full object-contain"
                                      />
                                    </Link>
                                    {/* Save for Later — only shown to authenticated users */}
                                    {userDoc && (
                                      <button
                                        onClick={() => {
                                          addProductToFavorites(userDoc.uid, item.product_id);
                                          removeFromCart(item.product_id);
                                        }}
                                        disabled={addingProductIds.has(item.product_id)}
                                        className="flex items-center gap-[4px] text-[10px] text-brand-primary hover:text-brand-primary/80 disabled:cursor-not-allowed disabled:text-gray-300"
                                      >
                                        <HeartIcon size={11} />
                                        <span>Save for Later</span>
                                      </button>
                                    )}
                                  </div>

                                  {/* Info column */}
                                  <div className="flex min-w-0 flex-1 flex-col gap-[4px]">
                                    {/* Title + Price on same row */}
                                    <div className="flex items-start justify-between gap-[8px]">
                                      <Link
                                        href={getProductUrl(slug)}
                                        onClick={closeDrawer}
                                        className="line-clamp-2 text-[11px] font-medium text-[#1B1B1B] hover:text-brand-primary hover:underline"
                                      >
                                        {item.product_title}
                                      </Link>
                                      <span className="shrink-0 text-right text-[13px] font-semibold text-[#1B1B1B]">
                                        {hasOriginalPrice && <span className="block text-[11px] font-normal text-gray-500 line-through">{formatCurrency(originalPrice * item.qty)}</span>}
                                        {formatCurrency(currentPrice * item.qty)}
                                      </span>
                                    </div>

                                    {/* Condition badge + Qty counter (only when qty > 1) */}
                                    <div className="flex items-center justify-between">
                                      <ConditionBadge condition={product?.condition} />
                                      {item.qty > 1 && (
                                        <div className="flex items-center gap-[6px] rounded-full border border-gray-300 px-[8px] py-[2px]">
                                          <button
                                            onClick={() => decreaseCartQty(item.product_id)}
                                            disabled={incrementingIds.has(item.product_id) || addingProductIds.has(item.product_id)}
                                            className="text-[12px] text-gray-500 hover:text-gray-800 disabled:cursor-not-allowed disabled:text-gray-300"
                                            aria-label="Decrease quantity"
                                          >
                                            &minus;
                                          </button>
                                          <span className="min-w-[14px] text-center text-[11px] font-medium text-[#1B1B1B]">
                                            {item.qty}
                                          </span>
                                          <button
                                            onClick={async () => {
                                              if (!product) return;
                                              // Also bail if a cross-surface add (e.g. PDP) is in
                                              // flight for this id — otherwise the tap is a silent
                                              // 'dropped' with no feedback (D5).
                                              if (
                                                incrementingIds.has(item.product_id) ||
                                                addingProductIds.has(item.product_id)
                                              )
                                                return;
                                              const cartItem = getCartItemFromProductDocument(product, getEffectivePriceForProduct(product) ?? undefined);
                                              if (!cartItem) return;
                                              setIncrementingIds((prev) =>
                                                new Set(prev).add(item.product_id)
                                              );
                                              try {
                                                // Pass the realtime product's stock so the guest
                                                // branch (no fetch) can cap the client-side bump.
                                                await increaseCartQty(cartItem, true, product.stock);
                                              } finally {
                                                setIncrementingIds((prev) => {
                                                  const next = new Set(prev);
                                                  next.delete(item.product_id);
                                                  return next;
                                                });
                                              }
                                            }}
                                            disabled={incrementingIds.has(item.product_id) || addingProductIds.has(item.product_id)}
                                            className="text-[12px] text-gray-500 hover:text-gray-800 disabled:cursor-not-allowed disabled:text-gray-300"
                                            aria-label="Increase quantity"
                                          >
                                            +
                                          </button>
                                        </div>
                                      )}
                                    </div>

                                    {/* Shipping + delivery — tight together, extra gap above */}
                                    <div className="mt-[4px] flex flex-col gap-0">
                                      <div className="flex items-center gap-[4px]">
                                        {isFreeShipping ? (
                                          <span className="flex items-center gap-[4px] text-[10px] font-medium text-green-600">
                                            <TruckIcon size={12} className="text-green-600" />
                                            Free Shipping
                                          </span>
                                        ) : (
                                          <span className="flex items-center gap-[4px] text-[10px] text-gray-500">
                                            <TruckIcon size={12} className="text-gray-400" />
                                            {shippingBase != null
                                              ? `Est. Shipping + Insurance: ${formatCurrency(shippingBase.cost)}`
                                              : 'Shipping calculated at checkout'}
                                          </span>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-[4px] text-[10px] text-gray-500">
                                        <CalendarIcon size={11} />
                                        Est. delivery: {deliveryRange}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* 3. Pricing Summary */}
              <div className="mt-[20px] flex flex-col gap-[10px] px-[14px] py-[12px]">
                {/* Subtotal */}
                <div className="flex items-center justify-between text-[13px]">
                  <span className="text-gray-700">
                    Subtotal ({visibleItems.reduce((sum, i) => sum + i.qty, 0)} {visibleItems.reduce((sum, i) => sum + i.qty, 0) === 1 ? 'item' : 'items'})
                  </span>
                  <span className="font-semibold text-[#1B1B1B]">{formatCurrency(subtotal)}</span>
                </div>
                {/* Shipping — hidden on mobile */}
                <div className="hidden items-center justify-between text-[13px] lg:flex">
                  <span className="text-gray-700">Shipping</span>
                  {allFreeShipping ? (
                    <span className="font-medium text-green-600">Free</span>
                  ) : (
                    // The exact destination total isn't known in the drawer (no
                    // address), so show the honest summary rather than a number
                    // derived from the unresolved item.shipping_cost.
                    <span className="text-[12px] italic text-gray-400">Calculated at checkout</span>
                  )}
                </div>
                {/* Tax — hidden on mobile */}
                <div className="hidden items-center justify-between text-[13px] lg:flex">
                  <span className="text-gray-700">Tax</span>
                  <span className="text-[12px] italic text-gray-400">Calculated at checkout</span>
                </div>
              </div>

              {/* 4. Affirm messaging — mobile + desktop, provider-rendered (GB #1372).
                  Wrapper only renders when the eligibility gate will actually
                  show something: AffirmMessaging returns null under
                  AFFIRM_DISPLAY_MIN, and an unconditional wrapper left a
                  zero-height div whose margin doubled the gap to Continue
                  Shopping below (QA follow-up). buyerCountry reuses the same
                  derivation as the shipping band above so a non-US buyer
                  doesn't see US-only financing here that vanishes at the
                  payment step. isAffirmDisplayable folds the country gate
                  into the wrapper decision too (Codex P3, GB #1372 QA
                  follow-up) — gating on amount alone still left the gap for
                  a known non-US buyer, since AffirmMessaging would render
                  null for them regardless of amount. */}
              {isAffirmDisplayable(estimatedTotal, buyerCountry) && (
                <div className="mt-[16px] flex items-center justify-center">
                  <AffirmMessaging amountUSD={estimatedTotal} buyerCountry={buyerCountry} />
                </div>
              )}

              {/* 6. Continue Shopping — desktop only */}
              <div className="mt-[16px] hidden justify-center lg:flex">
                <button
                  onClick={closeDrawer}
                  className="flex items-center gap-[6px] text-[14px] font-medium text-gray-500 hover:text-[#1B1B1B]"
                >
                  <ArrowLeftIcon size={16} />
                  Continue Shopping
                </button>
              </div>
            </div>

            {/* ================================================================
                STICKY FOOTER
            ================================================================ */}
            <div className="shrink-0 border-t border-gray-200 bg-white px-[20px] pb-[20px] pt-[16px]">
              {/* Items-only subtotal — shipping + tax calculated at checkout */}
              <div className="mb-[12px] flex items-center justify-between">
                <span className="text-[14px] font-bold text-gray-700">Subtotal</span>
                <span className="text-[20px] font-bold text-[#1B1B1B]">{formatCurrency(subtotal)}</span>
              </div>

              {/* Proceed to Checkout — navy background */}
              <Link
                href="/checkout"
                onClick={closeDrawer}
                className="mb-[12px] flex h-[41px] w-full items-center justify-center rounded-[8px] bg-[#242E5F] text-[14px] font-semibold text-white transition-colors hover:bg-[#1a2147]"
              >
                Proceed to Checkout
              </Link>

              {/* Payment icons */}
              <PaymentIcons />
            </div>
          </>
        )}
      </div>
    </BaseDrawer>

    {/* Buyer Protection modal — z-modal (400) to render above the cart drawer
        (z-overlay, 300) it's opened from. */}
    <div className="relative z-modal">
      <ProtectionPolicyModal
        isOpen={protectionModalOpen}
        dismiss={() => setProtectionModalOpen(false)}
      />
    </div>
    </>
  );
};

export default CartSlideOutV3;
